import React, { useState, useEffect } from 'react';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { groupsAPI } from '../../../services/api';
import MemberSelectionModal from '../Members/MemberSelectionModal';
import { formatGroupType } from '../../../utils/formatters';
import LocationSelector from '../../common/Forms/LocationSelector';
import './AddGroupForm.css'; // Reuse the same CSS

function EditGroupForm({ onClose, onGroupUpdated, group }) {
    const [formData, setFormData] = useState({
        name: group.name || '',
        type: group.type || 'club',
        country: group.country || '',
        city: group.city || '',
        description: group.description || '',
        logoUrl: group.logoUrl || null,
        members: group.members || [],
        socialLinks: {
            facebook: group.socialLinks?.facebook || '',
            instagram: group.socialLinks?.instagram || ''
        }
    });
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(group.logoUrl || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showMemberSelection, setShowMemberSelection] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('social-')) {
            const platform = name.replace('social-', '');
            setFormData(prev => ({
                ...prev,
                socialLinks: {
                    ...prev.socialLinks,
                    [platform]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5242880) { // 5MB limit
                setError('Logo size should be less than 5MB');
                return;
            }
            setLogo(file);
            setLogoPreview(URL.createObjectURL(file));
            setError('');
        }
    };

    const handleMemberSelection = (selectedMembers) => {
        setFormData(prev => ({
            ...prev,
            members: selectedMembers
        }));
        setShowMemberSelection(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let updatedData = { ...formData };
            
            if (logo) {
                const storage = getStorage();
                const logoRef = storageRef(storage, `groups/${Date.now()}_${logo.name}`);
                await uploadBytes(logoRef, logo);
                const logoUrl = await getDownloadURL(logoRef);
                updatedData.logoUrl = logoUrl;
            }
            
            await groupsAPI.updateGroup(group.id, updatedData);
            onGroupUpdated();
            onClose();
        } catch (error) {
            setError(error.message || 'Failed to update group');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-group-form-overlay">
            <div className="add-group-form">
                <h2>Edit Group</h2>
                {error && <div className="error-message">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Group Type *</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="club">{formatGroupType('club')}</option>
                            <option value="localCouncil">{formatGroupType('localCouncil')}</option>
                            <option value="projectTeam">{formatGroupType('projectTeam')}</option>
                            <option value="cityTeam">{formatGroupType('cityTeam')}</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Location</label>
                        <LocationSelector
                            countryCode={formData.country}
                            city={formData.city}
                            onCountryChange={(code) => {
                                setFormData(prev => ({
                                    ...prev,
                                    country: code,
                                    city: ''
                                }));
                            }}
                            onCityChange={(cityName) => {
                                setFormData(prev => ({
                                    ...prev,
                                    city: cityName
                                }));
                            }}
                        />
                    </div>

                    <div className="form-group">
                        <label>Description *</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                            rows="4"
                        />
                    </div>

                    <div className="form-group">
                        <label>Members</label>
                        <button 
                            type="button" 
                            className="select-members-button"
                            onClick={() => setShowMemberSelection(true)}
                        >
                            Select Members ({formData.members.length} selected)
                        </button>
                    </div>

                    <div className="form-group">
                        <label>Logo</label>
                        <div className="logo-upload-container">
                            {logoPreview ? (
                                <div className="logo-preview">
                                    <img src={logoPreview} alt="Logo preview" />
                                    <button 
                                        type="button" 
                                        className="remove-logo"
                                        onClick={() => {
                                            setLogo(null);
                                            setLogoPreview(null);
                                            setFormData(prev => ({ ...prev, logoUrl: null }));
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ) : (
                                <div className="logo-upload-box">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                        id="logo-upload"
                                        className="hidden-input"
                                    />
                                    <label htmlFor="logo-upload" className="upload-label">
                                        <i className="fas fa-cloud-upload-alt"></i>
                                        <span>Click to upload logo</span>
                                        <span className="upload-hint">Recommended: 400x400px</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Facebook Link</label>
                        <input
                            type="url"
                            name="social-facebook"
                            value={formData.socialLinks.facebook}
                            onChange={handleInputChange}
                            placeholder="https://facebook.com/..."
                        />
                    </div>

                    <div className="form-group">
                        <label>Instagram Link</label>
                        <input
                            type="url"
                            name="social-instagram"
                            value={formData.socialLinks.instagram}
                            onChange={handleInputChange}
                            placeholder="https://instagram.com/..."
                        />
                    </div>

                    <div className="form-actions">
                        <button type="submit" disabled={loading}>
                            {loading ? 'Updating...' : 'Update Group'}
                        </button>
                        <button type="button" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            {showMemberSelection && (
                <MemberSelectionModal
                    currentMembers={formData.members}
                    onSelect={handleMemberSelection}
                    onClose={() => setShowMemberSelection(false)}
                />
            )}
        </div>
    );
}

export default EditGroupForm; 